import { createContext, useContext, useState, type ReactNode } from 'react';

import { jwtDecode } from 'jwt-decode';

import { api } from '@/lib/api-client';

interface SessionInfo {
  sessionToken: string;
  username: string;
  /** Azure AD display name. Absent on sessions opened before it was kept. */
  displayName?: string;
}

interface SessionTokenResponse {
  session_token: string;
}

interface AuthState {
  isAuthenticated: boolean;
  username: string | null;
  displayName: string | null;
  isLoading: boolean;
  error: string | null;
  shouldRedirectToLogin: boolean;
}

interface AuthContextType extends AuthState {
  signin: (
    username: string,
    idToken: string,
    displayName?: string,
  ) => Promise<void>;
  signout: () => void;
  checkAuth: (showAlert?: boolean) => boolean;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const SESSION_STORAGE_KEY = 'sessionInfo';

/**
 * Validates a JWT token and checks if it's expired
 */
function isValidToken(token: string | undefined): boolean {
  if (!token) return false;

  try {
    const decoded = jwtDecode(token);
    const now = Date.now();

    if (decoded.exp) {
      return decoded.exp * 1000 > now;
    }

    return false;
  } catch (error) {
    console.error('Token validation failed:', error);
    return false;
  }
}

/**
 * Retrieves session info from localStorage
 */
function getStoredSession(): SessionInfo | null {
  try {
    const session = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!session) return null;

    return JSON.parse(session) as SessionInfo;
  } catch (error) {
    console.error('Failed to parse stored session:', error);
    return null;
  }
}

/**
 * Stores session info in localStorage
 */
function storeSession(session: SessionInfo): void {
  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
}

/**
 * Removes session info from localStorage
 */
function clearSession(): void {
  localStorage.removeItem(SESSION_STORAGE_KEY);
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [authState, setAuthState] = useState<AuthState>(() => {
    // Initialize state from localStorage
    const session = getStoredSession();
    if (session && isValidToken(session.sessionToken)) {
      return {
        isAuthenticated: true,
        username: session.username,
        displayName: session.displayName ?? null,
        isLoading: false,
        error: null,
        shouldRedirectToLogin: false,
      };
    }

    // Clear invalid session
    if (session) {
      clearSession();
    }

    return {
      isAuthenticated: false,
      username: null,
      displayName: null,
      isLoading: false,
      error: null,
      shouldRedirectToLogin: false,
    };
  });

  /**
   * Check authentication status
   * This is a pure function that returns the current auth state
   */
  const checkAuth = (showAlert = false): boolean => {
    const session = getStoredSession();

    if (!session || !isValidToken(session.sessionToken)) {
      // Clean up if token is invalid or expired
      if (authState.isAuthenticated) {
        clearSession();

        // Show alert if requested and not on login page
        if (showAlert) {
          alert(
            'Your session has expired. You will be redirected to the login screen.',
          );

          // Set flag to trigger redirect in NavigationGuard
          setAuthState({
            isAuthenticated: false,
            username: null,
            displayName: null,
            isLoading: false,
            error: 'Session expired',
            shouldRedirectToLogin: true,
          });
        } else {
          setAuthState({
            isAuthenticated: false,
            username: null,
            displayName: null,
            isLoading: false,
            error: 'Session expired',
            shouldRedirectToLogin: false,
          });
        }
      }
      return false;
    }

    return true;
  };

  /**
   * Sign in with username and ID token
   */
  const signin = async (
    username: string,
    idToken: string,
    displayName?: string,
  ): Promise<void> => {
    setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      // Exchange ID token for session token
      const response = await api.get<SessionTokenResponse>(
        new URL('v1/login/azuread', import.meta.env.VITE_ADMIN_API_URL).href,
        {
          params: { access_token: idToken },
        },
      );

      const sessionToken = response.data.session_token;
      const session: SessionInfo = {
        sessionToken,
        username,
        displayName,
      };

      // Store session and update state
      storeSession(session);

      setAuthState({
        isAuthenticated: true,
        username,
        displayName: displayName ?? null,
        isLoading: false,
        error: null,
        shouldRedirectToLogin: false,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Authentication failed';

      setAuthState({
        isAuthenticated: false,
        username: null,
        displayName: null,
        isLoading: false,
        error: errorMessage,
        shouldRedirectToLogin: false,
      });

      throw new Error(errorMessage);
    }
  };

  /**
   * Sign out and clear session
   */
  const signout = (): void => {
    clearSession();
    setAuthState({
      isAuthenticated: false,
      username: null,
      displayName: null,
      isLoading: false,
      error: null,
      shouldRedirectToLogin: false,
    });
  };

  /**
   * Clear error state
   */
  const clearError = (): void => {
    setAuthState((prev) => ({ ...prev, error: null }));
  };

  const value: AuthContextType = {
    ...authState,
    signin,
    signout,
    checkAuth,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to access auth context
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
